import {Injectable} from '@angular/core';
import {Apollo, gql} from 'apollo-angular';
import {ToastrService} from 'ngx-toastr';
import {catchError, EMPTY, filter, map, Observable} from 'rxjs';
import {TemplateEditMode} from 'src/app/views/builder/builder-form/builder-form.component';
import {notEmpty} from '../utils/utils';
import {ErrorService} from './error.service';
import {
  TemplateService_GetTemplateItem,
  TemplateService_GetTemplateItemVariables,
  TemplateService_GetTemplateItem_templateItem,
} from './__generated__/TemplateService_GetTemplateItem';
import {LabInfo, LabInputTypeOption, getParametersFromForm} from '@vasio-nl/skideo-create-lib';
import {UntypedFormGroup} from '@angular/forms';
import {
  TemplateService_CreateUpdateBuilderTemplate,
  TemplateService_CreateUpdateBuilderTemplateVariables,
  TemplateService_CreateUpdateBuilderTemplate_updateOrCreateBuilderTemplate_templateItem,
} from './__generated__/TemplateService_CreateUpdateBuilderTemplate';
import {BuilderParameterInput} from '../graphql/globalTypes';
import {
  TemplateService_DeleteTemplateItemContent,
  TemplateService_DeleteTemplateItemContentVariables,
} from '../services/__generated__/TemplateService_DeleteTemplateItemContent';
import {labelFrag, tagFrag} from '../components/multi-select/multi-select.component';

const GET_TEMPLATE_ITEM_QUERY = gql`
  query TemplateService_GetTemplateItem($id: ID!) {
    templateItem(id: $id) {
      id
      title
      builderVisibleFields
      type
      builderConfigId
      createPassword
      archived
      lookupId
      template {
        id
        created
        title
        thumbnail
      }
      parameters {
        edges {
          node {
            id
            name
            defaultValue
            locked
          }
        }
      }
      labels {
        edges {
          node {
            ...MultiSelectComponentLabelFragment
          }
        }
      }
      tags {
        edges {
          node {
            ...MultiSelectComponentTagFragment
          }
        }
      }
    }
  }
  ${labelFrag}
  ${tagFrag}
`;

const CREATE_UPDATE_BUILDER_TEMPLATE_MUTATION = gql`
  mutation TemplateService_CreateUpdateBuilderTemplate($input: BuilderTemplateInput) {
    updateOrCreateBuilderTemplate(input: $input) {
      templateItem {
        id
        title
        createPassword
        type
        parameters {
          edges {
            node {
              id
              name
              locked
              defaultValue
            }
          }
        }
        labels {
          edges {
            node {
              ...MultiSelectComponentLabelFragment
            }
          }
        }
        tags {
          edges {
            node {
              ...MultiSelectComponentTagFragment
            }
          }
        }
      }
    }
  }
  ${labelFrag}
  ${tagFrag}
`;

const DELETE_CONTENT_MUTATION = gql`
  mutation TemplateService_DeleteTemplateItemContent($id: ID, $reason: String) {
    deleteContentOfTemplateItem(id: $id, reason: $reason) {
      templateItem {
        id
      }
      error
    }
  }
`;

@Injectable({
  providedIn: 'root',
})
export class TemplateService {
  constructor(
    private apollo: Apollo,
    private toastr: ToastrService,
    private errorService: ErrorService,
  ) {}

  getTemplateItem$(
    templateItemId: string,
  ): Observable<TemplateService_GetTemplateItem_templateItem> {
    return this.apollo
      .watchQuery<TemplateService_GetTemplateItem, TemplateService_GetTemplateItemVariables>({
        query: GET_TEMPLATE_ITEM_QUERY,
        variables: {id: templateItemId},
      })
      .valueChanges.pipe(
        map(({data}) => data?.templateItem),
        filter(notEmpty),
        catchError(error => {
          this.errorService.handleDefault(error);
          this.toastr.warning($localize`An error occurred retrieving the template item.`);
          return EMPTY;
        }),
      );
  }

  createUpdateBuilderTemplate$(
    id: string,
    labInfo: LabInfo,
    form: UntypedFormGroup,
    mode: TemplateEditMode,
    templateName: string,
    password: string | null,
    dynamicFormValues: {[key: string]: string},
  ): Observable<TemplateService_CreateUpdateBuilderTemplate_updateOrCreateBuilderTemplate_templateItem> {
    const dynamicFieldNames = Object.keys(dynamicFormValues);
    const inputParams: BuilderParameterInput[] = getParametersFromForm(
      labInfo,
      form,
      dynamicFieldNames,
    );

    // Needed for the URL export on the template-details page. Only non-hidden and dynamic fields are included.
    const visibleControlNames: string[] = this.getPreviewUrlParameterNames(labInfo)
      .concat(dynamicFieldNames);

    const dynamicParams: BuilderParameterInput[] = [];
    if (dynamicFieldNames.length > 0) {
      Object.keys(dynamicFormValues).forEach(fieldName =>
        dynamicParams.push({
          name: fieldName,
          defaultValue: dynamicFormValues[fieldName],
          locked: false,
        }),
      );
    }

    return this.apollo
      .mutate<
        TemplateService_CreateUpdateBuilderTemplate,
        TemplateService_CreateUpdateBuilderTemplateVariables
      >({
        mutation: CREATE_UPDATE_BUILDER_TEMPLATE_MUTATION,
        variables: {
          input: {
            id: mode === TemplateEditMode.EDIT ? id : undefined,
            builderOptionId: mode === TemplateEditMode.NEW ? id : undefined,
            title: templateName,
            createPassword: password === '' ? null : password,
            parameters: inputParams.concat(dynamicParams),
            builderVisibleFields: visibleControlNames,
          },
        },
        update: cache => {
          cache.evict({id: 'ROOT_QUERY', fieldName: 'templates'}); // Updates template overview when adding/removing new templates.
          cache.evict({id: 'ROOT_QUERY', fieldName: 'templateItem'}); // Updates password when going back to builder form.
        },
      })
      .pipe(
        catchError(error => {
          this.errorService.handleDefault(error);
          return EMPTY;
        }),
        map(data => data.data?.updateOrCreateBuilderTemplate.templateItem),
        filter(notEmpty),
      );
  }

  deleteTemplateItemContent$(
    id: string,
    reason?: string,
  ): Observable<Apollo.MutateResult<TemplateService_DeleteTemplateItemContent>> {
    return this.apollo.mutate<
      TemplateService_DeleteTemplateItemContent,
      TemplateService_DeleteTemplateItemContentVariables
    >({
      mutation: DELETE_CONTENT_MUTATION,
      variables: {id, reason},
      update: cache => cache.evict({id: 'ROOT_QUERY', fieldName: 'templates'}),
    });
  }

  private getPreviewUrlParameterNames(labInfo: LabInfo): string[] {
    const previewUrlExcludedInputTypes = new Set<string>([
      LabInputTypeOption.LAB_SELECTION,
      LabInputTypeOption.IMAGE_UPLOAD,
      LabInputTypeOption.VIDEO_UPLOAD,
      LabInputTypeOption.AI_GENERATOR,
      LabInputTypeOption.DYNAMIC_REFERENCE_TEXTAREA,
    ]);
    
    return labInfo.inputs
      .filter(input => input.hidden !== true && !previewUrlExcludedInputTypes.has(input.type))
      .map(input => input.name);
  }
}
